import { Component, Input, OnInit, OnChanges, SimpleChanges, ChangeDetectionStrategy } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";

import { ProductDownloadsBaseComponent } from "@mypxplat/xplat/features";
import { UserService } from "@mypxplat/xplat/core";
import { AppService } from "@mypxplat/xplat/web/core";

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: "myp-product-downloads",
  templateUrl: "product-downloads.component.html",
  standalone: false,
})
export class ProductDownloadsComponent extends ProductDownloadsBaseComponent implements OnInit, OnChanges {
  @Input() downloads: any;
  @Input() downloadSections: any;
  @Input() product: any;
  @Input() dealer: boolean = false;
  public displayedDownloadSection: any;
  // The currently displayed section's items, filtered by search term and sorted — same
  // sort_by/sort_direction settings myp-product-filter already drives elsewhere (orders,
  // admin product lists), reused as-is here rather than inventing a separate sort scheme.
  public filteredDownloads: any[] = [];
  // Based on the displayed section's total item count, not the post-search-filter count — the
  // bar shouldn't disappear just because a search narrows the visible results down to one.
  public showDownloadFilter: boolean = false;
  private _searchTerm: string = "";

  constructor(
    userService: UserService,
    public appService: AppService,
    private translate: TranslateService
  ) {
    super(userService);
  }

  ngOnInit() {
    if (this.downloadSections?.length) {
      this._sortDownloadSectionsAlphabetically();
      this.displayDownloadSection(this.downloadSections[0]);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["downloadSections"]) {
      const sections = changes["downloadSections"].currentValue;
      if (sections?.length && !this.displayedDownloadSection) {
        this._sortDownloadSectionsAlphabetically();
        this.displayDownloadSection(this.downloadSections[0]);
      } else if (!sections?.length) {
        this.displayedDownloadSection = undefined;
        this.filteredDownloads = [];
        this.showDownloadFilter = false;
      }
    }
  }

  // Orders the section tabs alphabetically by their displayed label — the custom `label` when set,
  // otherwise the translated "download.<value>" string the template shows. Keeps the same section
  // object references (shallow copy) so displayed-state tracking is unaffected.
  private _sortDownloadSectionsAlphabetically() {
    const sectionLabel = (section: any): string => (section.label || this.translate.instant("download." + section.value) || section.value || "").toLowerCase();
    this.downloadSections = [...this.downloadSections].sort((a, b) => sectionLabel(a).localeCompare(sectionLabel(b)));
  }

  displayDownloadSection(section?, stringId?) {
    if (this.downloadSections.length) {
      if (section) {
        this.downloadSections.forEach((item) => (item.displayed = false));
        section.displayed = true;
        this.displayedDownloadSection = section;
      } else if (stringId) {
        const section = this.downloadSections.find((item) => item.value === stringId);
        if (section) {
          this.downloadSections.forEach((item) => (item.displayed = false));
          section.displayed = true;
          this.displayedDownloadSection = section;
        }
      }
      this._applyFilterAndSort();
    }
  }

  filterDownloads(term?: string) {
    this._searchTerm = (term || "").toLowerCase();
    this._applyFilterAndSort();
  }

  sortDownloads() {
    this._applyFilterAndSort();
  }

  private _applyFilterAndSort() {
    const source: any[] = (this.displayedDownloadSection && this.downloads?.[this.displayedDownloadSection.value]) || [];
    this.showDownloadFilter = source.length > 1;
    const term = this._searchTerm;
    const filtered = term ? source.filter((dl) => (dl.title || "").toLowerCase().includes(term) || (dl.filename || dl.fileName || "").toLowerCase().includes(term)) : [...source];

    filtered.sort((a, b) => {
      if (this.appService.sortBy === "date") {
        const dateA = a.releaseDate ? new Date(a.releaseDate).getTime() : 0;
        const dateB = b.releaseDate ? new Date(b.releaseDate).getTime() : 0;
        if (dateA === dateB) return 0;
        return this.appService.sortDirection === "asc" ? dateA - dateB : dateB - dateA;
      }
      const nameA = (a.title || a.filename || a.fileName || "").toLowerCase();
      const nameB = (b.title || b.filename || b.fileName || "").toLowerCase();
      if (nameA === nameB) return 0;
      return this.appService.sortDirection === "asc" ? (nameA < nameB ? -1 : 1) : nameA < nameB ? 1 : -1;
    });

    this.filteredDownloads = filtered;
  }
}
